// admin-ui/src/marketing/preview-pages.ts
/** Pages for the signed-out flip preview: the cover (page 1, always),
 *  whole magazine spreads (an even left-hand page and the odd page
 *  after it), and optionally one last left-hand page that faces the
 *  "Keep reading" page — at most 8 pages with the cover, the same shape
 *  as the default 1–8. The flip viewer shows page 1 alone and then pairs
 *  2–3, 4–5 … Mirrors the server's checks in marketing_picks.py so
 *  staff see the problem before saving. */
export const MAX_PREVIEW_PAGES = 8;

export type ParseResult =
  | { ok: true; spreads: number[]; single: number | null }
  | { ok: false; error: string };

export function parsePreviewPages(input: string, pageCount: number | null): ParseResult {
  const spreads: number[] = [];
  let single: number | null = null;
  const tokens = input.split(",").map((t) => t.trim()).filter(Boolean);
  if (tokens.length === 0) return { ok: false, error: "Enter at least one spread, e.g. 14–17." };
  for (const [i, token] of tokens.entries()) {
    const range = /^(\d+)\s*[-–—]\s*(\d+)$/.exec(token);
    const one = /^(\d+)$/.exec(token);
    if (!range && !one) return { ok: false, error: `"${token}" isn't a page or a range like 14–17.` };
    if (one) {
      const n = Number(one[1]);
      if (n === 1) return { ok: false, error: "Page 1 (the cover) is always shown." };
      if (i !== tokens.length - 1) {
        return { ok: false, error: `Page ${n} on its own can only be the last page (it faces Keep reading); use a spread like ${n % 2 ? n - 1 : n}–${n % 2 ? n : n + 1}.` };
      }
      if (n % 2) return { ok: false, error: `Page ${n}: the last single page is a left-hand (even) page.` };
      if (pageCount && n > pageCount) return { ok: false, error: `Page ${n}: the issue has ${pageCount} pages.` };
      single = n;
      continue;
    }
    const a = Number(range![1]);
    const b = Number(range![2]);
    if (a === 1) return { ok: false, error: "Page 1 (the cover) is always shown — start from page 2." };
    if (a % 2) return { ok: false, error: `${token}: a range starts on a left-hand (even) page.` };
    if (b <= a || b % 2 === 0) return { ok: false, error: `${token}: a range ends on a right-hand (odd) page after its start.` };
    if (pageCount && b > pageCount) return { ok: false, error: `${token}: the issue has ${pageCount} pages.` };
    // Check the size before expanding: a typo like 2-99999999 would
    // otherwise loop for millions of pages on every keystroke.
    if ((b - a + 1) / 2 > MAX_PREVIEW_PAGES) {
      return { ok: false, error: `${token}: more than ${MAX_PREVIEW_PAGES} pages including the cover.` };
    }
    for (let s = a; s < b; s += 2) {
      if (spreads.includes(s)) return { ok: false, error: `Pages ${s}–${s + 1} are listed twice.` };
      spreads.push(s);
    }
  }
  spreads.sort((x, y) => x - y);
  if (single !== null && spreads.length && single <= spreads[spreads.length - 1] + 1) {
    return { ok: false, error: `Page ${single}: the single page comes after the spreads.` };
  }
  const total = 1 + 2 * spreads.length + (single !== null ? 1 : 0);
  if (total > MAX_PREVIEW_PAGES) {
    return { ok: false, error: `At most ${MAX_PREVIEW_PAGES} pages including the cover — that's ${total}.` };
  }
  return { ok: true, spreads, single };
}

export function formatPreview(spreads: readonly number[], single: number | null): string {
  const parts: string[] = [];
  let start: number | null = null;
  let prev = 0;
  for (const s of spreads) {
    if (start !== null && s === prev + 2) { prev = s; continue; }
    if (start !== null) parts.push(`${start}–${prev + 1}`);
    start = s;
    prev = s;
  }
  if (start !== null) parts.push(`${start}–${prev + 1}`);
  if (single !== null) parts.push(String(single));
  return parts.join(", ");
}

export function previewPageList(spreads: readonly number[], single: number | null): number[] {
  return [1, ...spreads.flatMap((s) => [s, s + 1]), ...(single !== null ? [single] : [])];
}

type PreviewMeta = { spreads?: number[]; single?: number; toc_page?: number } | null | undefined;

/** The panel's input text for a saved preview. */
export function formFromMeta(meta: PreviewMeta): { pages: string; toc: string } {
  if (!meta) return { pages: "", toc: "" };
  return {
    pages: formatPreview(meta.spreads ?? [], meta.single ?? null),
    toc: meta.toc_page ? String(meta.toc_page) : "",
  };
}

/** Changes whenever the saved preview changes (including a colleague's
 *  edit picked up by a refetch), so the panel re-fills its inputs. */
export function metaKey(meta: PreviewMeta): string {
  return JSON.stringify(meta ?? null);
}
